import { useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Eye } from "lucide-react";
import { formatMoney, formatDate } from "@/lib/api";
import ShareWeekModal from "./ShareWeekModal";

// Shows the CLOSEST future week (0..12 ahead) that projects tight or short.
// Hidden entirely when the horizon is all-safe, so it never nags without a
// reason. The card itself opens Year At A Glance; a dedicated Share button
// on the right lets the user send this week to a partner as a public link.
// When the user has previously shared this same week, a small "N views"
// pill next to the Share button tells them whether the link actually landed.
export default function NextTightWeekBanner({ data, mySnapshots = [], onSnapshotCreated }) {
  const [sharing, setSharing] = useState(false);
  const weeks = data?.weeks || [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const idx = weeks.findIndex((w) => {
    if (w.status === "safe") return false;
    const end = new Date(w.week_end + "T00:00:00");
    return end >= today;
  });
  if (idx === -1) return null;
  const w = weeks[idx];
  const isShort = w.status === "short";
  const startDate = new Date(w.week_start + "T00:00:00");
  const daysAway = Math.max(0, Math.round((startDate - new Date()) / 86400000));
  const awayLabel = daysAway === 0 ? "this week" : daysAway <= 7 ? "next week" : `in ~${Math.round(daysAway / 7)} weeks`;
  const bg = isShort ? "bg-[#FEF3EE] border-[#F0C8B8]" : "bg-[#FFF9EA] border-[#E9D69F]";
  const dotColor = isShort ? "bg-[#8C3A30]" : "bg-[#B48A24]";
  const textAccent = isShort ? "text-[#7A2E24]" : "text-[#7A4E1F]";
  // Hover tooltip surfaces WHY this week is flagged so users understand
  // "tight" vs "short" without opening Year at a Glance first.
  const tooltip = isShort
    ? `Short week — projected balance dips below $0 (${formatMoney(w.end_balance)}). Click to open Year At A Glance for the full breakdown.`
    : `Tight week — balance stays positive but drops low (${formatMoney(w.end_balance)}). Click to open Year At A Glance for the full breakdown.`;
  // Aggregate view counts for any snapshots the user has already made that
  // match THIS week's start date. Shows "3 views" next to the Share button
  // so creators see whether shared links actually landed. Match is by
  // week_start because the shared payload is the whole week card.
  const matchingSnaps = (mySnapshots || []).filter(
    (s) => s?.payload?.week_start === w.week_start,
  );
  const totalViews = matchingSnaps.reduce((sum, s) => sum + Number(s.view_count || 0), 0);
  const activeShares = matchingSnaps.length;
  return (
    <>
      <div
        className={`card-soft p-4 border ${bg} flex items-center gap-3 flex-wrap`}
        data-testid="dashboard-next-tight-banner"
      >
        <Link
          to="/app/year"
          title={tooltip}
          className="flex items-center gap-3 flex-wrap flex-1 min-w-0 hover:brightness-95 transition-all -m-1 p-1 rounded-lg"
          onClick={() => {
            try { window.localStorage.setItem("wmrb_year_pinned_week", String(idx)); } catch { /* ignore */ }
          }}
          data-testid="dashboard-next-tight-link"
        >
          <span className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
          <div className={`text-[10px] font-semibold uppercase tracking-widest ${textAccent} whitespace-nowrap`}>
            Heads up — {isShort ? "short" : "tight"} week ahead
          </div>
          <div className={`text-sm font-semibold ${textAccent} whitespace-nowrap`} data-testid="dashboard-next-tight-when">
            {formatDate(w.week_start)} → {formatDate(w.week_end)} · {awayLabel}
          </div>
          <div className="text-xs text-[#59605C]" data-testid="dashboard-next-tight-detail">
            <span className="whitespace-nowrap">Projected end-balance <span className={`font-semibold ${isShort ? "text-[#8C3A30]" : "text-[#7A4E1F]"}`}>{formatMoney(w.end_balance)}</span></span>
            {w.bill_count > 0 && <span className="whitespace-nowrap"> · {w.bill_count} {w.bill_count === 1 ? "bill" : "bills"} due</span>}
          </div>
        </Link>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setSharing(true); }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A6148] hover:text-[#3B5B3E] hover:bg-white/60 px-2.5 py-1.5 rounded-full transition-colors shrink-0"
          title={activeShares > 0
            ? `You've shared this week ${activeShares} ${activeShares === 1 ? "time" : "times"}. Views so far: ${totalViews}. Click to create another share link.`
            : "Send this week to a partner as a public link"}
          data-testid="dashboard-next-tight-share"
        >
          <Share2 size={14} /> Share this week
        </button>
        {activeShares > 0 && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#4A6148] bg-white/70 px-2 py-0.5 rounded-full shrink-0"
            title={`${totalViews} total view${totalViews === 1 ? "" : "s"} across ${activeShares} share link${activeShares === 1 ? "" : "s"}`}
            data-testid="dashboard-next-tight-views"
          >
            <Eye size={12} /> {totalViews} view{totalViews === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {sharing && (
        <ShareWeekModal
          week={w}
          onClose={() => setSharing(false)}
          onCreated={onSnapshotCreated}
        />
      )}
    </>
  );
}
